import { DisplayNameSource, type UserProfileDto } from '@today/types'
import { ProfileSettingsView } from '@today/ui-domain'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useProfileForm } from '@/hooks/useUserProfile'
import { SETTINGS_NAMESPACE } from '@/i18n'

/** Native language names: each is shown in its own language. */
const LANGUAGES = [
	{ id: 'es', text: 'Español' },
	{ id: 'gl', text: 'Galego' },
	{ id: 'en', text: 'English' },
	{ id: 'pt', text: 'Português' },
]

/** The IANA zones this browser knows, plus a saved zone it may not list. */
const timezonesWith = (saved: string | null): string[] => {
	const zones = [...Intl.supportedValuesOf('timeZone'), 'UTC']
	return saved && !zones.includes(saved) ? [saved, ...zones] : zones
}

/** The Settings "Profile" section (TO-197 REQ-010). */
export function ProfileSettings({ profile }: { profile: UserProfileDto | undefined }) {
	const { t } = useTranslation(SETTINGS_NAMESPACE)
	const form = useProfileForm(profile)
	const savedZone = profile?.timezone ?? null
	const timezoneOptions = useMemo(
		() => [{ id: '', text: t('profile.deviceTimezone') }, ...timezonesWith(savedZone).map((id) => ({ id, text: id }))],
		[t, savedZone],
	)
	return (
		<ProfileSettingsView
			displayName={form.draft.name}
			onDisplayNameChange={form.setName}
			displayNameError={form.nameError ? t('profile.nameInvalid') : null}
			overridden={profile?.displayNameSource === DisplayNameSource.USER}
			idpDisplayName={profile?.idpDisplayName ?? ''}
			onReset={form.reset}
			language={form.draft.language}
			languageOptions={[{ id: '', text: t('profile.automatic') }, ...LANGUAGES]}
			onLanguageChange={form.setLanguage}
			timezone={form.draft.timezone}
			timezoneOptions={timezoneOptions}
			onTimezoneChange={form.setTimezone}
			onSave={form.save}
			isSaving={form.isSaving}
			error={form.hasFailed ? (form.error ?? t('profile.saveFailed')) : null}
			saved={form.saved}
			labels={{
				title: t('profile.title'),
				description: t('profile.description'),
				displayName: t('profile.displayName'),
				idpDefault: t('profile.idpDefault'),
				reset: t('profile.reset'),
				language: t('profile.language'),
				timezone: t('profile.timezone'),
				save: t('profile.save'),
				saved: t('profile.saved'),
			}}
		/>
	)
}
